import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Sparkles, Loader2, Check, X, Wand2, Play, Settings2, FileText, MessageCircle, Send, Rocket } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const PRIORITIES = [
  { key: "P0", label: "Must-do", cls: "border-red-500/50 bg-red-500/10 text-red-500" },
  { key: "P1", label: "Strong", cls: "border-amber-500/50 bg-amber-500/10 text-amber-500" },
  { key: "P2", label: "Polish", cls: "border-sky-500/50 bg-sky-500/10 text-sky-500" },
];

function DesignDoc({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div className="space-y-1" data-testid="studio-design-doc">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (t.startsWith("##")) return <h3 key={i} className="font-display text-sm font-bold mt-3 text-[hsl(var(--foreground))]">{t.replace(/^#+\s*/, "")}</h3>;
        if (t.startsWith("- ") || t.startsWith("• ")) return <div key={i} className="flex gap-2 text-sm text-[hsl(var(--muted-foreground))]"><span className="text-emerald-500">•</span><span>{t.replace(/^[-•]\s*/, "")}</span></div>;
        return <p key={i} className="text-sm text-[hsl(var(--muted-foreground))]">{t}</p>;
      })}
    </div>
  );
}

export default function AIStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [specialists, setSpecialists] = useState([]);
  const [studio, setStudio] = useState(null);
  const [brief, setBrief] = useState("");
  const [convening, setConvening] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [draftRoster, setDraftRoster] = useState([]);
  const [applyingId, setApplyingId] = useState(null);
  const [autoBuilding, setAutoBuilding] = useState(false);

  // Specialist chat state
  const [chatRole, setChatRole] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatApplyingIdx, setChatApplyingIdx] = useState(null);
  const scrollRef = useRef(null);

  const specById = useMemo(() => Object.fromEntries(specialists.map((s) => [s.id, s])), [specialists]);

  const load = useCallback(async () => {
    try {
      const [g, roster, st] = await Promise.all([
        api.get(`/games/${gameId}`),
        api.get(`/studio/roster`),
        api.get(`/studio/${gameId}`),
      ]);
      setGame(g.data);
      setSpecialists(roster.data?.specialists || []);
      setStudio(st.data);
      setBrief(st.data?.brief || "");
      setDraftRoster(st.data?.roster || []);
    } catch (e) {
      toast.error("Couldn't open the studio for this game.");
    } finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const chatHistory = useMemo(() => (chatRole && studio?.chats?.[chatRole]) || [], [chatRole, studio]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chatHistory, chatSending, chatRole]);

  const convene = async () => {
    if (!brief.trim()) { toast.error("Give the studio a brief first — what should this game become?"); return; }
    setConvening(true);
    try {
      const { data } = await api.post(`/studio/${gameId}/plan`, { brief: brief.trim() }, { timeout: 120000 });
      setStudio(data);
      toast.success("The war room delivered a plan! Review the tasks below.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "The studio couldn't convene right now."); }
    finally { setConvening(false); }
  };

  const saveRoster = async () => {
    try {
      const { data } = await api.post(`/studio/${gameId}/hire`, { roles: draftRoster });
      setStudio(data);
      setManageOpen(false);
      toast.success("Roster updated!");
    } catch (e) { toast.error("Couldn't update the roster."); }
  };

  const toggleDraft = (id) => setDraftRoster((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  // Shared apply — runs a single AI edit instruction against the game.
  const runInstruction = async (instruction) => {
    const { data } = await api.post(`/games/${gameId}/edit`, { instruction }, { timeout: 120000 });
    return data;
  };

  const applyTask = async (task) => {
    setApplyingId(task.id);
    try {
      const data = await runInstruction(task.instruction);
      if (data?.clarify) {
        toast.message("Studio needs a detail", { description: data.summary });
        return;
      }
      await api.post(`/studio/${gameId}/task/${task.id}`, { status: "applied" });
      setStudio((s) => ({ ...s, tasks: s.tasks.map((t) => (t.id === task.id ? { ...t, status: "applied" } : t)) }));
      toast.success(`✅ ${data?.summary || "Applied to your game!"}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? "That task couldn't be applied — try a simpler brief." : formatApiError(d));
      toast.error(msg || "Couldn't apply that task.");
    }
    finally { setApplyingId(null); }
  };

  const dismissTask = async (task) => {
    setStudio((s) => ({ ...s, tasks: s.tasks.map((t) => (t.id === task.id ? { ...t, status: "dismissed" } : t)) }));
    try { await api.post(`/studio/${gameId}/task/${task.id}`, { status: "dismissed" }); } catch { /* optimistic */ }
  };

  const tasks = studio?.tasks || [];
  const activeTasks = tasks.filter((t) => t.status !== "dismissed");
  const appliedCount = tasks.filter((t) => t.status === "applied").length;
  const pendingP0 = activeTasks.filter((t) => t.priority === "P0" && t.status !== "applied");

  const autoBuild = async () => {
    if (!pendingP0.length) { toast.message("No pending must-do (P0) tasks to auto-build."); return; }
    setAutoBuilding(true);
    let done = 0;
    for (const t of pendingP0) {
      setApplyingId(t.id);
      try {
        const data = await runInstruction(t.instruction);
        if (data?.clarify) { continue; }
        await api.post(`/studio/${gameId}/task/${t.id}`, { status: "applied" });
        setStudio((s) => ({ ...s, tasks: s.tasks.map((x) => (x.id === t.id ? { ...x, status: "applied" } : x)) }));
        done++;
      } catch (e) { /* keep going through the plan */ }
    }
    setApplyingId(null);
    setAutoBuilding(false);
    toast.success(`🚀 Auto-build complete — applied ${done}/${pendingP0.length} must-do task${pendingP0.length === 1 ? "" : "s"}.`);
  };

  // ---- Specialist chat ----
  const openChat = (role) => { setChatRole(role); setChatInput(""); };
  const closeChat = () => setChatRole(null);

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatSending || !chatRole) return;
    const prev = studio?.chats?.[chatRole] || [];
    setChatInput("");
    setChatSending(true);
    setStudio((s) => ({ ...s, chats: { ...(s?.chats || {}), [chatRole]: [...prev, { from: "you", text: msg }] } }));
    try {
      const { data } = await api.post(`/studio/${gameId}/chat`, { role: chatRole, message: msg }, { timeout: 120000 });
      setStudio((s) => ({ ...s, chats: { ...(s?.chats || {}), [chatRole]: data.history || [] } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Your specialist got distracted — try again in a moment.");
      setStudio((s) => ({ ...s, chats: { ...(s?.chats || {}), [chatRole]: prev } }));
      setChatInput(msg);
    } finally { setChatSending(false); }
  };

  const applyFromChat = async (idx, instruction) => {
    setChatApplyingIdx(idx);
    try {
      const data = await runInstruction(instruction);
      if (data?.clarify) {
        toast.message("Needs a detail", { description: data.summary });
        return;
      }
      toast.success(`✅ ${data?.summary || "Applied to your game!"}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : formatApiError(d);
      toast.error(msg || "Couldn't apply that suggestion.");
    } finally { setChatApplyingIdx(null); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  const roster = studio?.roster || [];
  const chatSpec = chatRole ? specById[chatRole] : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="ai-studio">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="studio-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Users className="w-6 h-6 text-violet-500" /> AI Studio</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{game?.title || "Your game"} · your AI dev-team war room</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate(`/app/play/${gameId}`)} data-testid="studio-play-btn"><Play className="w-4 h-4" /> Play</Button>
      </div>

      {/* Roster */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Your studio ({roster.length}) · tap a teammate to chat</p>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setDraftRoster(roster); setManageOpen((v) => !v); }} data-testid="studio-manage-roster-btn"><Settings2 className="w-4 h-4" /> Manage roster</Button>
        </div>
        {!manageOpen ? (
          <div className="flex flex-wrap gap-2" data-testid="studio-roster">
            {roster.map((id) => {
              const s = specById[id];
              if (!s) return null;
              return (
                <button
                  key={id}
                  onClick={() => openChat(id)}
                  className="group inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-3 py-1.5 text-xs font-semibold transition-colors hover:border-violet-500/60 hover:bg-violet-500/10"
                  data-testid={`studio-chat-open-${id}`}
                >
                  <span>{s.emoji} {s.name}</span>
                  <MessageCircle className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] group-hover:text-violet-500 transition-colors" />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3" data-testid="studio-roster-manage">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {specialists.map((s) => {
                const on = draftRoster.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggleDraft(s.id)} data-testid={`studio-hire-${s.id}`} className={`text-left rounded-xl border p-3 transition-colors ${on ? "border-violet-500 bg-violet-500/10" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{s.emoji} {s.name}</span>
                      {on && <Check className="w-4 h-4 text-violet-500 shrink-0" />}
                    </div>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">Focuses on {s.focus}.</p>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="rounded-full" onClick={saveRoster} data-testid="studio-save-roster-btn">Save roster ({draftRoster.length})</Button>
              <Button variant="ghost" size="sm" onClick={() => setManageOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Brief / war room */}
      <div className="rounded-2xl border border-violet-500/40 bg-gradient-to-br from-violet-500/10 to-sky-500/5 p-4 mb-5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400 mb-2 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> Convene the war room</p>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder="Brief your studio… e.g. 'Turn this into a tense survival-horror with a terrifying multi-phase boss finale.'"
          maxLength={800}
          rows={3}
          data-testid="studio-brief-input"
          className="w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm resize-none"
        />
        <div className="flex items-center justify-between gap-2 mt-2">
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Your specialists collaborate and turn this into applyable tasks.</span>
          <Button className="rounded-full" onClick={convene} disabled={convening} data-testid="studio-convene-btn">
            {convening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {convening ? "Convening…" : "Convene war room"}
          </Button>
        </div>
      </div>

      {/* Design doc */}
      {studio?.design_doc && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-5" data-testid="studio-design-doc-panel">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Living design doc</p>
          <DesignDoc text={studio.design_doc} />
        </div>
      )}

      {/* Task board */}
      {activeTasks.length > 0 ? (
        <div className="space-y-5" data-testid="studio-task-board">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display text-lg font-bold">Build plan</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="studio-applied-count">{appliedCount} applied</span>
              <Button
                size="sm"
                className="rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white"
                onClick={autoBuild}
                disabled={autoBuilding || !pendingP0.length}
                data-testid="studio-autobuild-btn"
              >
                {autoBuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {autoBuilding ? "Building…" : `Auto-build P0 (${pendingP0.length})`}
              </Button>
            </div>
          </div>
          {PRIORITIES.map((pri) => {
            const group = activeTasks.filter((t) => t.priority === pri.key);
            if (!group.length) return null;
            return (
              <div key={pri.key} data-testid={`studio-group-${pri.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${pri.cls}`}>{pri.key}</span>
                  <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">{pri.label}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {group.map((t) => {
                    const s = specById[t.role];
                    const applied = t.status === "applied";
                    return (
                      <div key={t.id} className={`rounded-2xl border p-4 transition-colors ${applied ? "border-emerald-500/40 bg-emerald-500/5" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`} data-testid={`studio-task-${t.id}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{s ? `${s.emoji} ${s.name}` : t.role}</span>
                          {applied && <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-500" data-testid={`studio-task-applied-${t.id}`}><Check className="w-3.5 h-3.5" /> Applied</span>}
                        </div>
                        <h3 className="font-display text-sm font-bold mb-1">{t.title}</h3>
                        {t.detail && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{t.detail}</p>}
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))] italic bg-[hsl(var(--secondary))] rounded-lg px-2.5 py-1.5 mb-3">“{t.instruction}”</p>
                        {!applied && (
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="rounded-full flex-1" onClick={() => applyTask(t)} disabled={applyingId === t.id || autoBuilding} data-testid={`studio-task-apply-${t.id}`}>
                              {applyingId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {applyingId === t.id ? "Applying…" : "Apply to game"}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-9 w-9 text-[hsl(var(--muted-foreground))]" onClick={() => dismissTask(t)} disabled={autoBuilding} data-testid={`studio-task-dismiss-${t.id}`}><X className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !convening && (
          <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center" data-testid="studio-empty">
            <Users className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Brief your studio above and hit <b>Convene war room</b> — your specialists will draft a design doc and a prioritized build plan you can apply in one click. Or tap any teammate to <b>chat 1:1</b> and generate content mid-build.</p>
          </div>
        )
      )}

      {/* Specialist chat side panel */}
      {chatSpec && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" onClick={closeChat} data-testid="studio-chat-scrim" />
          <div
            className="fixed inset-y-0 right-0 z-[70] w-full sm:w-[440px] bg-[hsl(var(--card))] border-l border-[hsl(var(--border))] shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
            data-testid="studio-chat-panel"
          >
            <div className="flex items-center gap-3 p-4 border-b border-[hsl(var(--border))]">
              <div className="w-10 h-10 rounded-full bg-violet-500/15 grid place-items-center text-lg shrink-0">{chatSpec.emoji}</div>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-bold truncate">{chatSpec.name}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">Focuses on {chatSpec.focus}.</p>
              </div>
              <Button variant="ghost" size="icon" className="shrink-0" onClick={closeChat} data-testid="studio-chat-close"><X className="w-5 h-5" /></Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="studio-chat-messages">
              {chatHistory.length === 0 && !chatSending && (
                <div className="text-center py-8">
                  <MessageCircle className="w-8 h-8 mx-auto text-[hsl(var(--muted-foreground))] mb-2" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Say hi to your {chatSpec.name.toLowerCase()}! Ask for ideas, or tell them to build something — e.g. <i>"Give me a fire-breathing dragon boss"</i>. Their answer will include a one-click <b>Apply</b>.</p>
                </div>
              )}
              {chatHistory.map((m, i) => {
                const mine = m.from === "you";
                return (
                  <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`studio-chat-msg-${i}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${mine ? "bg-violet-600 text-white rounded-br-sm" : "bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] rounded-bl-sm"}`}>
                      <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                      {!mine && m.apply && (
                        <Button
                          size="sm"
                          className="rounded-full mt-2.5 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white"
                          onClick={() => applyFromChat(i, m.apply)}
                          disabled={chatApplyingIdx === i}
                          data-testid={`studio-chat-apply-${i}`}
                        >
                          {chatApplyingIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {chatApplyingIdx === i ? "Applying…" : "Apply this to my game"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {chatSending && (
                <div className="flex justify-start" data-testid="studio-chat-typing">
                  <div className="rounded-2xl rounded-bl-sm bg-[hsl(var(--secondary))] px-4 py-3">
                    <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--muted-foreground))]" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-[hsl(var(--border))] flex items-end gap-2">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder={`Message your ${chatSpec.name.toLowerCase()}…`}
                rows={1}
                maxLength={600}
                data-testid="studio-chat-input"
                className="flex-1 resize-none rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2.5 text-sm max-h-32"
              />
              <Button size="icon" className="rounded-xl shrink-0 h-11 w-11" onClick={sendChat} disabled={chatSending || !chatInput.trim()} data-testid="studio-chat-send">
                {chatSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
